<!--
<COPYRIGHT>

    Copyright © 2016-2025, Canyon GBS LLC. All rights reserved.

    Notice:

    - This software is closed source and the source code is a trade secret.
    - You may not alter, remove, or obscure any licensing, copyright, or other notices
      of the licensor in the software.
    - Canyon GBS LLC respects the intellectual property rights of others and expects the
      same in return. Canyon GBS™ is a registered trademarks of Canyon GBS LLC, and we are
      committed to enforcing and protecting our trademarks vigorously.

    For more information or inquiries please visit our website at
    https://www.canyongbs.com or contact us via email at legal@canyongbs.com.

</COPYRIGHT>
-->
<script setup>
import { defineProps, onMounted, ref } from "vue";
import DOMPurify from "dompurify";

onMounted(async () => {
    await getData();
});

const props = defineProps(["url"]);

const scriptUrl = new URL(document.currentScript.getAttribute("src"));
const protocol = scriptUrl.protocol;
const scriptHostname = scriptUrl.hostname;

const hostUrl = `${protocol}//${scriptHostname}`;

const roadMapStatuses = ref([]);
const roadMaps = ref([]);
const errorMessage = ref(null);
const closedStatus = ref(null);
const loading = ref(false);

async function getData() {
    errorMessage.value = null;

    await fetch(props.url)
        .then(async (response) => {
            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error(await response.json());
                }

                throw new Error("An error occurred while fetching the data.");
            }

            return response.json();
        })
        .then((json) => {
            roadMaps.value = json.data.roadMaps || [];
            roadMapStatuses.value = json.data.roadMapStatus || [];
            closedStatus.value = json.data.closedClassification;
            loading.value = true;
        })
        .catch((error) => {
            errorMessage.value = error;
        });
}
</script>

<template>
    <div class="font-sans w-full sm:px-8 bg-white text-black pt-4">
        <link
            rel="stylesheet"
            v-bind:href="hostUrl + '/js/widgets/roadmap/style.css'"
        />
        <div class="mt-2 flex flex-col">
            <h1
                v-if="errorMessage"
                class="text-2xl font-bold text-red-500 mx-auto"
            >
                {{ errorMessage }}
            </h1>
            <div class="overflow-x-auto">
                <div class="inline-block min-w-full align-middle">
                    <div class="overflow-hidden shadow">
                        <div
                            class="mb-6 flex items-start justify-start space-x-4 px-4"
                        >
                            <div
                                class="min-w-kanban"
                                v-for="status in roadMapStatuses"
                                :key="status.id"
                            >
                                <div
                                    class="flex items-center justify-between gap-3"
                                >
                                    <div
                                        class="py-4 text-base font-semibold text-dark-900 dark:text-white-300"
                                    >
                                        {{ status.name }}
                                    </div>
                                </div>
                                <div
                                    class="relative flex flex-col gap-4 min-w-kanban mb-1 h-full"
                                    :class="{ 'pb-20': !roadMaps[status.name] }"
                                >
                                    <div v-if="roadMaps[status.name]">
                                        <div
                                            class="z-10 mb-4 flex w-80 transform flex-col rounded-lg bg-white p-5 shadow dark:bg-gray-800"
                                            v-for="roadmap in roadMaps[
                                                status.name
                                            ]"
                                            :key="roadmap.id"
                                        >
                                            <div>
                                                <div
                                                    class="flex items-center justify-between pb-4"
                                                >
                                                    <div
                                                        class="break-all text-base font-semibold text-gray-900 dark:text-white"
                                                    >
                                                        {{ roadmap.title }}
                                                    </div>
                                                </div>

                                                <div class="flex flex-col">
                                                    <div
                                                        class="pb-4 text-sm font-normal text-gray-700 dark:text-gray-400 break-words"
                                                    >
                                                        <div
                                                            v-html="
                                                                DOMPurify.sanitize(
                                                                    roadmap.description
                                                                )
                                                            "
                                                        ></div>
                                                    </div>

                                                    <div
                                                        class="flex justify-end"
                                                    >
                                                        <div
                                                            v-if="
                                                                closedStatus ===
                                                                roadmap.statusClassification
                                                            "
                                                            class="flex items-center justify-center rounded-lg bg-green-100 px-3 text-sm font-medium text-green-800 dark:bg-green-200"
                                                        >
                                                            <svg
                                                                class="mr-1 h-4 w-4"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill-rule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clip-rule="evenodd"
                                                                ></path>
                                                            </svg>
                                                            Done
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        v-else
                                        class="absolute flex h-20 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-2 font-semibold text-gray-500 hover:border-gray-300 dark:border-gray-800"
                                    >
                                        No roadmap items.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
